import { Injectable, Inject } from '@angular/core';
import { Roles } from 'src/shared/enums';
import {
	FieldTeamLink,
	FieldWorkerLink,
	UserClient,
	User,
} from 'src/shared/api';
import {
	map,
	filter,
	shareReplay,
	switchMap,
	distinctUntilChanged,
} from 'rxjs/operators';
import { Observable, of, BehaviorSubject } from 'rxjs';
import { throwIf } from 'src/shared/utilities/custom-rxjs-operators';
import {
	MsalBroadcastService,
	MsalGuardConfiguration,
	MsalService,
	MSAL_GUARD_CONFIG,
} from '@azure/msal-angular';
import {
	AccountInfo,
	InteractionStatus,
	RedirectRequest,
} from '@azure/msal-browser';

@Injectable({
	providedIn: 'root',
})
export class AuthService {
	private _userId = new BehaviorSubject<string>(null);
	private _isAuthenticated = new BehaviorSubject<boolean>(false);
	private _userProfile$ = this._userId.pipe(
		distinctUntilChanged(),
		switchMap((id) => (id ? this.userClient.getAdminUser(id) : of(null))),
		filter(Boolean),
		map((user: User) => user.fieldWorker),
		throwIf(
			(fieldWorker) => fieldWorker === null,
			'You are not linked to a Field Worker.'
		),
		map((fw) =>
			Object.assign(
				{},
				{
					id: fw ? fw.id : 0,
					name: `${fw.firstName} ${fw.lastName}`,
					firstName: fw.firstName,
					lastName: fw.lastName,
				}
			)
		),
		shareReplay(1)
	);

	public isHandleRedirectCompleted$ =
		this.msalBroadcastService.inProgress$.pipe(
			filter(
				(status: InteractionStatus) => status === InteractionStatus.None
			)
		);
	public isAuthenticated$ = this.isHandleRedirectCompleted$.pipe(
		switchMap((_) => this._isAuthenticated)
	);

	constructor(
		@Inject(MSAL_GUARD_CONFIG)
		private msalGuardConfig: MsalGuardConfiguration,
		private msalService: MsalService,
		private msalBroadcastService: MsalBroadcastService,
		private readonly userClient: UserClient
	) {}

	set adminUserTeamLink(team: FieldTeamLink) {
		if (team) {
			localStorage.setItem('team', JSON.stringify(team));
		}
	}

	get adminUserTeamLink(): FieldTeamLink {
		return JSON.parse(localStorage.getItem('team'));
	}

	get msalUserInfo(): AccountInfo {
		return this.msalService.instance.getActiveAccount();
	}

	get userProfile(): Observable<FieldWorkerLink> {
		return this._userProfile$;
	}

	get userRoles(): string[] {
		const user: any = this.msalUserInfo;
		if (!user || !user.idTokenClaims) {
			return [];
		}
		return user.idTokenClaims.roles;
	}

	get userId(): string {
		let _userId = null;
		if (this.msalUserInfo) {
			_userId = this.msalUserInfo.localAccountId;
		}
		return _userId;
	}

	get isUserRolePayroll(): boolean {
		return (
			this.userRoles.includes(Roles.Admin) ||
			this.userRoles.includes(Roles.Payroll)
		);
	}

	get isUserRoleHR(): boolean {
		return this.userRoles.includes(Roles.HR);
	}

	isUserInOneOfTheseRoles(roles: string[]) {
		return this.userRoles.some((r) => roles.includes(r));
	}

	msalLogin(): Observable<boolean> {
		if (this.msalService.instance.getTokenCache()) {
			return this.msalService
				.acquireTokenRedirect({
					...this.msalGuardConfig.authRequest,
				} as RedirectRequest)
				.pipe(map(() => true));
		} else {
			return this.msalService
				.loginRedirect({
					...this.msalGuardConfig.authRequest,
				} as RedirectRequest)
				.pipe(map(() => true));
		}
	}

	logout(): void {
		this.msalService.logout();
		this.nextUserId(null);
		this.nextAuthenticated(false);
	}

	setActiveAccount() {
		const accounts = this.msalService.instance.getAllAccounts();
		if (accounts.length > 0) {
			const account = accounts[0];
			this.msalService.instance.setActiveAccount(account);
			this.nextAuthenticated(true);
			this.nextUserId(account.localAccountId);
		}
	}

	nextUserId(userId: string | null) {
		this._userId.next(userId);
	}

	nextAuthenticated(val: boolean) {
		this._isAuthenticated.next(val);
	}
}
